import { create } from "zustand";
import AuthService from "../api/AppService";
import { User } from "@myapp/contract";

type LogoutReason = 'archived' | 'session_expired';

interface AuthState {
  user: User | null;
  isLoading: boolean;        // request in progress
  isInitialized: boolean;    // first auth check finished
  isAuthenticated: boolean;
  authError: boolean;
  logoutReason: LogoutReason | null;

  login: (email: string, password: string) => Promise<void>;
  checkAuth: () => Promise<void>;
  logout: (reason?: LogoutReason) => Promise<void>;
  clearLogoutReason: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  isInitialized: false,
  isAuthenticated: false,
  authError: false,
  logoutReason: null,

  login: async (email, password) => {
    set({ isLoading: true, authError: false });

    try {
      const { data } = await AuthService.login(email, password);

      set({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        logoutReason: null,
      });
    } catch (error) {
      set({
        isLoading: false,
        authError: true,
        isInitialized: true,
        user: null,
        isAuthenticated: false,
      });
      throw error;
    }
  },

  checkAuth: async () => {
    // prevent duplicate calls (StrictMode / rerenders)
    if (get().isLoading || get().isInitialized) return;

    set({ isLoading: true });

    try {
      const { data } = await AuthService.refresh();

      set({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
      });
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
      });
    }
  },

  logout: async (reason?: LogoutReason) => {
    try {
      await AuthService.logout();
    } catch {
      // best-effort — clear local state regardless
    }
    set({
      user: null,
      isAuthenticated: false,
      isInitialized: true,
      logoutReason: reason ?? null,
    });
  },

  clearLogoutReason: () => set({ logoutReason: null }),
}));
